import * as React from 'react';
import { AnalyticsTrigger } from '@core-front/analytics';
import { usePrevious } from '@core-front/helper';
import PropsChecker from '@core-front/props-checker';
const ANIMATION_WAVE_DELAY = 100;
const ANIMATION_IN_DURATION = 300;
const ANIMATION_OUT_DURATION = 400;
const OptionCardCore = (props) => {
    return (React.createElement(PropsChecker, { componentName: "OptionCard", componentProps: props, defaultValueName: "defaultChecked", valueName: "checked", onChangeNames: "onChange", isComponentDisabled: props.disabled },
        React.createElement(AnalyticsTrigger, { trigger: {
                callback: 'onChange',
                id: ([checked]) => `Option - ${checked ? 'Check' : 'Uncheck'} - ${props.id}`,
                elementId: props.id,
                elementType: 'option',
                interactionType: ([checked]) => `${checked ? 'check' : 'uncheck'}`,
            } },
            React.createElement(OptionCardCoreInner, Object.assign({}, props)))));
};
const OptionCardCoreInner = (props) => {
    const { background, borderRadius, className, checked, children, components, defaultChecked, disabled, elevation, id, index, input, inputType, isInvalid, labelText, padding, responsive, style, type, value = 'on', } = props;
    const [animationActive, setAnimationActive] = React.useState(false);
    const [animationDirection, setAnimationDirection] = React.useState('in');
    const [isCardChecked, setIsCardChecked] = React.useState(defaultChecked || false);
    const [directionTimeout, setDirectionTimeout] = React.useState(0);
    const onClick = () => onChange();
    const { Icon, Card } = components;
    const isChecked = props.onChange && typeof checked !== 'undefined' ? checked : isCardChecked;
    const inputRef = React.useRef(null);
    let waveTimeout = 0;
    const prevIsInvalid = usePrevious(isInvalid);
    React.useEffect(() => {
        if (!prevIsInvalid && isInvalid) {
            startWaveAnimation();
        }
        else if (prevIsInvalid && !isInvalid) {
            endAnimation();
            clearTimeouts();
        }
        return () => {
            clearTimeouts();
        };
    });
    const clearTimeouts = () => {
        if (directionTimeout) {
            window.clearTimeout(directionTimeout);
        }
        if (waveTimeout) {
            window.clearTimeout(waveTimeout);
        }
    };
    const onChange = (event) => {
        const nextChecked = 
        // radio can only be checked, not unchecked
        inputType === 'radio'
            ? true
            : // select via keyboard
                event
                    ? event.currentTarget.checked
                    : // select via click
                        inputRef.current
                            ? !inputRef.current.checked
                            : // fallback to state value
                                !isChecked;
        if (disabled || (inputType === 'radio' && isChecked === nextChecked)) {
            return;
        }
        if (typeof checked === 'undefined') {
            setIsCardChecked(nextChecked);
        }
        if (props.onChange) {
            props.onChange(nextChecked);
        }
    };
    const BEM = () => {
        const classArray = ['option-card', `option-card--input-${inputType}`];
        if (background) {
            classArray.push('option-card--' + background);
        }
        if (type) {
            classArray.push('option-card--' + type);
        }
        if (className) {
            classArray.push(className);
        }
        if (isInvalid) {
            classArray.push('option-card--error');
        }
        if (animationActive) {
            classArray.push('is-animating');
        }
        if (animationDirection) {
            classArray.push('option-card--animate-' + animationDirection);
        }
        if (isChecked) {
            classArray.push('is-checked');
        }
        return classArray.join(' ');
    };
    const startWaveAnimation = () => {
        if (isInvalid && typeof index !== 'undefined') {
            waveTimeout = window.setTimeout(() => {
                startAnimation();
            }, index * ANIMATION_WAVE_DELAY);
        }
    };
    const startAnimation = () => {
        setAnimationActive(true);
        setAnimationDirection('in');
        triggerFadeIn();
    };
    const endAnimation = () => {
        setAnimationActive(false);
        setAnimationDirection('idle');
    };
    const triggerFadeIn = () => {
        setDirectionTimeout(window.setTimeout(() => {
            setAnimationDirection('out');
            triggerFadeOut();
        }, ANIMATION_IN_DURATION));
    };
    const triggerFadeOut = () => {
        setDirectionTimeout(window.setTimeout(() => {
            endAnimation();
        }, ANIMATION_OUT_DURATION));
    };
    const renderIndicator = () => {
        if (!isCardChecked && disabled) {
            return;
        }
        return React.createElement("span", { className: "option-card__indicator" }, inputType === 'checkbox' && React.createElement(Icon, { modifiers: "#check", className: "option-card__indicator-icon" }));
    };
    return (React.createElement("div", { className: BEM(), onClick: onClick },
        React.createElement("input", Object.assign({}, input, { value: value, type: inputType, ref: inputRef, onChange: onChange, onClick: event => event.stopPropagation(), checked: isChecked, defaultChecked: undefined, disabled: disabled, id: id, className: "option-card__input", "aria-invalid": isInvalid })),
        React.createElement("label", { htmlFor: id, className: "option-card__label" }, labelText),
        React.createElement(Card, { className: "option-card__body", responsive: responsive, padding: padding, borderRadius: borderRadius, style: style, elevation: disabled ? undefined : elevation },
            React.createElement("div", { className: "option-card__elements" },
                renderIndicator(),
                children))));
};
export { OptionCardCore as default };
